<template>
  <div class="login-page">
    <div class="login-left">
      <div class="login-form-wrap">
        <div class="logo-wrap mb-10">
          <v-img src="/logo.png" max-width="180" contain></v-img>
        </div>

        <div class="mb-8">
          <h1 class="login-title">Willkommen zurück</h1>
          <p class="login-subtitle">Bitte melden Sie sich an, um fortzufahren.</p>
        </div>

        <v-alert
          v-if="errorMessage"
          type="error"
          variant="tonal"
          closable
          class="mb-5"
          density="compact"
          @click:close="errorMessage = null"
        >
          {{ errorMessage }}
        </v-alert>

        <form @submit.prevent="doLogin()">
          <div class="field-group mb-4">
            <label class="field-label">E-Mail</label>
            <div class="field-wrap" :class="{ 'field-error': emailError, 'field-focus': emailFocused }">
              <v-icon size="16" color="rgba(255,255,255,0.35)" class="field-icon">mdi-email-outline</v-icon>
              <input
                v-model="email"
                type="email"
                class="field-input"
                placeholder="name@firma.de"
                :disabled="loading"
                @focus="emailFocused = true"
                @blur="emailFocused = false; validateEmail()"
              />
            </div>
            <div v-if="emailError" class="field-error-text">{{ emailError }}</div>
          </div>

          <div class="field-group mb-5">
            <label class="field-label">Passwort</label>
            <div class="field-wrap" :class="{ 'field-error': passwordError, 'field-focus': passwordFocused }">
              <v-icon size="16" color="rgba(255,255,255,0.35)" class="field-icon">mdi-lock-outline</v-icon>
              <input
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                class="field-input"
                placeholder="••••••••"
                :disabled="loading"
                @focus="passwordFocused = true"
                @blur="passwordFocused = false; validatePassword()"
              />
              <v-icon
                size="16"
                color="rgba(255,255,255,0.35)"
                class="field-toggle"
                @click="showPassword = !showPassword"
              >
                {{ showPassword ? 'mdi-eye-outline' : 'mdi-eye-off-outline' }}
              </v-icon>
            </div>
            <div v-if="passwordError" class="field-error-text">{{ passwordError }}</div>
          </div>

          <div class="d-flex align-center justify-space-between mb-8">
            <label class="remember-label">
              <input v-model="rememberMe" type="checkbox" class="remember-check" :disabled="loading" />
              <span>Angemeldet bleiben</span>
            </label>
          </div>

          <button type="submit" class="login-btn" :disabled="loading">
            <v-progress-circular v-if="loading" indeterminate size="18" width="2" color="#0a1628"></v-progress-circular>
            <span v-else>Anmelden</span>
          </button>
        </form>

        <div class="login-footer">
          <span>FLOOW · Section Manager</span>
        </div>
      </div>
    </div>

    <div class="login-right">
      <div class="right-card">
        <div class="right-bg" :style="{ backgroundImage: `url(${bgImage})` }"></div>
        <div class="right-overlay"></div>
        <div class="right-content">
          <div class="right-badge">
            <v-icon size="16" color="white">mdi-view-grid-outline</v-icon>
            Section Manager
          </div>
          <h2 class="right-title">Verwalten Sie Ihre Shopify-Sections effizient und sicher.</h2>
          <p class="right-desc">Sections, Kategorien, Mitarbeiter, Reviews und Live-Chat – zentral verwaltet, schnell auffindbar und für das gesamte Team an einem Ort.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useUserStore } from "@/store/index";
import axios from "axios";
import sha256 from "sha256";
import bgImage from "@/assets/background.png";

export default {
  name: "Login",

  data: () => ({
    email: "",
    password: "",
    rememberMe: false,
    bgImage,
    showPassword: false,
    errorMessage: null,
    loading: false,
    emailError: null,
    passwordError: null,
    emailFocused: false,
    passwordFocused: false,
  }),

  mounted() {
    const rememberedEmail = localStorage.getItem("rememberedEmail");
    if (rememberedEmail) {
      this.email = rememberedEmail;
      this.rememberMe = true;
    }
  },

  methods: {
    validateEmail() {
      if (!this.email) { this.emailError = "E-Mail ist erforderlich"; return false; }
      if (!/^.+@.+\..+$/.test(this.email)) { this.emailError = "E-Mail muss gültig sein"; return false; }
      this.emailError = null;
      return true;
    },

    validatePassword() {
      if (!this.password) { this.passwordError = "Passwort ist erforderlich"; return false; }
      this.passwordError = null;
      return true;
    },

    async doLogin() {
      const emailOk = this.validateEmail();
      const passOk = this.validatePassword();
      if (!emailOk || !passOk) return;

      this.loading = true;
      this.errorMessage = null;
      const userStore = useUserStore();

      try {
        const result = await axios.get("/api/authorization/login", {
          params: {
            email: this.email,
            password: sha256(this.password),
          },
        });

        if (result.data.success) {
          const { id, firstname, lastname, email, state, position } = result.data.user;
          userStore.setUser({ id, firstname, lastname, email, state, position });

          if (this.rememberMe) {
            localStorage.setItem("rememberedEmail", this.email);
          } else {
            localStorage.removeItem("rememberedEmail");
          }

          this.$router.push("/");
        } else {
          this.errorMessage = "Ungültige Anmeldedaten";
        }
      } catch (e) {
        console.error("Login Fehler:", e);
        this.errorMessage = "Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.login-page {
  display: flex;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
  position: fixed;
  top: 0;
  left: 0;
}

.login-left {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  background: #0a1628;
  position: relative;
  z-index: 1;
}

.login-form-wrap {
  width: 100%;
  max-width: 400px;
}

.login-title {
  font-size: 28px;
  font-weight: 800;
  color: white;
  letter-spacing: -0.4px;
  line-height: 1.2;
  margin-bottom: 8px;
}

.login-subtitle {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.45);
  font-weight: 400;
}

.field-group {
  display: flex;
  flex-direction: column;
}

.field-label {
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 6px;
  letter-spacing: 0.3px;
}

.field-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 46px;
  padding: 0 14px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  transition: all 0.15s ease;
}

.field-wrap.field-focus {
  border-color: rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.1);
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.05);
}

.field-wrap.field-error {
  border-color: #EF5350;
  background: rgba(239, 83, 80, 0.06);
}

.field-icon {
  flex-shrink: 0;
}

.field-input {
  flex: 1;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: white;
  font-weight: 500;
}

.field-input::placeholder {
  color: rgba(255, 255, 255, 0.25);
  font-weight: 400;
}

.field-input:disabled {
  opacity: 0.5;
}

.field-toggle {
  cursor: pointer;
  flex-shrink: 0;
}

.field-error-text {
  font-size: 11px;
  color: #EF5350;
  margin-top: 4px;
  font-weight: 500;
}

.remember-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  user-select: none;
}

.remember-check {
  width: 16px;
  height: 16px;
  accent-color: #60a5fa;
  cursor: pointer;
}

.login-btn {
  width: 100%;
  height: 46px;
  border: none;
  border-radius: 10px;
  background: white;
  color: #0a1628;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: 0.01em;
}

.login-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.9);
  transform: translateY(-1px);
}

.login-btn:active:not(:disabled) {
  transform: scale(0.98);
}

.login-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.login-footer {
  text-align: center;
  margin-top: 48px;
  font-size: 11px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.15);
  letter-spacing: 0.5px;
}

.login-right {
  flex: 1;
  padding: 20px 20px 20px 0;
  background: #0a1628;
}

.right-card {
  width: 100%;
  height: 100%;
  border-radius: 20px;
  position: relative;
  overflow: hidden;
}

.right-bg {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  z-index: 1;
}

.right-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(10, 22, 40, 0.2) 0%, rgba(10, 22, 40, 0.8) 100%);
  z-index: 2;
}

.right-content {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 48px;
  z-index: 3;
}

.right-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  color: white;
  margin-bottom: 20px;
}

.right-title {
  font-size: 24px;
  font-weight: 700;
  color: white;
  line-height: 1.3;
  letter-spacing: -0.2px;
  margin-bottom: 12px;
}

.right-desc {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.5;
  max-width: 400px;
}

@media (max-width: 959px) {
  .login-page {
    flex-direction: column;
  }

  .login-right {
    display: none;
  }

  .login-left {
    padding: 24px;
  }

  .login-title {
    font-size: 24px;
  }
}
</style>
